import { DownloadItemIcon } from '@/assets/static/icons/download-item';
import Button from '@/components/basicComponents/button';
import { Loader } from '@/components/loaders/loader';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { SORT_ORDER, TableColumns } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DOCUMENT_FIELD_RENDER_ORDER_QUERY, PUBLISHED_INVESTMENT_MANAGER_UPDATES_QUERY } from '../../queries';
import { PublishedInvestmentManagerUpdatesDto, PublishedInvestmentManagerUpdatesSourceEnum } from '../../types';
import PublishedUpdatesTabDownload from './modals/publishedUpdatesTabDownload';
import { PublishedUpdatesTable } from './table/table';
import PublishedUpdatesMobileTable from './table/tableMobile';

export const PublishedUpdatesTab = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { searchParams, onChangeSort, saveScrollPostion, makeScroll } = useSearchParamsManager();

  const position = searchParams.get('position') || 0;
  const key = searchParams.get('key') || 'STRATEGY';
  const asc = searchParams.get('asc') === null || searchParams.get('asc') === 'true';

  const [publishedUpdatesTableColumns, setPublishedUpdatesTableColumns] = useState<TableColumns[]>([]);
  const [publishedUpdates, setPublishedUpdates] = useState<PublishedInvestmentManagerUpdatesDto[]>([]);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });

  const { loading: documentFieldRenderOrderLoading } = useQuery(DOCUMENT_FIELD_RENDER_ORDER_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const updatedTableColumns: TableColumns[] = data?.documentFieldRenderOrder?.map((column, index) => ({
        title: column.name,
        key: null as string,
        keySort: column.name,
        width: index === 0 ? 277 : 200
      }));

      setPublishedUpdatesTableColumns([
        { title: 'Strategy', key: 'STRATEGY', width: 400, keySort: 'strategy', isFixed: true },
        ...updatedTableColumns,
        { title: 'Last Update', key: null, width: 200, keySort: 'last update' }
      ]);
    }
  });

  const { loading: publishedInvestmentManagerUpdatesLoading } = useQuery<{
    publishedInvestmentManagerUpdates: PublishedInvestmentManagerUpdatesDto[];
  }>(PUBLISHED_INVESTMENT_MANAGER_UPDATES_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      data: {
        publishedUpdatesOnly: true,
        sortDirection: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC
      }
    },
    onCompleted: (data) => {
      setPublishedUpdates(data?.publishedInvestmentManagerUpdates);
      makeScroll(position);
    }
  });

  const openOpportunityDetailsPage = (investmentId: string) => {
    saveScrollPostion();
    navigate(`/strategyDetails/${investmentId}?tab=Updates&backToTitle=Manager Updates`);
  };

  const openModalWindow = (type: string) => {
    setModalWindow({ isOpen: true, type });
  };

  const closeModalWindow = () => {
    setModalWindow({ isOpen: false, type: '' });
  };

  if (documentFieldRenderOrderLoading) return <LoaderOnWholeScreen />;

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'download' && (
        <PublishedUpdatesTabDownload
          isOpen={modalWindow.isOpen}
          publishedUpdatesTableColumns={publishedUpdatesTableColumns}
          publishedUpdates={publishedUpdates}
          fileName="Published Updates"
          source={PublishedInvestmentManagerUpdatesSourceEnum.MANAGER_UPDATES}
          onClose={closeModalWindow}
        />
      )}
      <PublishedUpdatesTabWrapper>
        {isMobile ? (
          <>
            {publishedInvestmentManagerUpdatesLoading ? (
              <Loader />
            ) : (
              <PublishedUpdatesMobileTable
                columns={publishedUpdatesTableColumns}
                publishedUpdates={publishedUpdates}
                source={PublishedInvestmentManagerUpdatesSourceEnum.MANAGER_UPDATES}
                openOpportunityDetailsPage={openOpportunityDetailsPage}
              />
            )}
          </>
        ) : (
          <PublishedUpdatesTable
            columns={publishedUpdatesTableColumns}
            publishedUpdates={publishedUpdates}
            sort={{ key, asc }}
            loading={publishedInvestmentManagerUpdatesLoading}
            source={PublishedInvestmentManagerUpdatesSourceEnum.MANAGER_UPDATES}
            setSort={onChangeSort}
            openOpportunityDetailsPage={openOpportunityDetailsPage}
          />
        )}
        <DownloadButtonWrapper>
          <Button onClick={() => openModalWindow('download')} size="lg" icon={<DownloadItemIcon fill={themeVar('context.light')} />}>
            Download
          </Button>
        </DownloadButtonWrapper>
      </PublishedUpdatesTabWrapper>
    </>
  );
};

const PublishedUpdatesTabWrapper = styled.div`
  padding: 50px 0;
`;

const DownloadButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
`;
